import { useContext, useEffect, useState } from 'react';
import { Grid } from '@mui/material';
import ListItemText from '@mui/material/ListItemText';
import ListItemButton from '@mui/material/ListItemButton';
import List from '@mui/material/List';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { SttSelectMship } from '../../sporttia/selectors/SttSelectMship';
import { AppContext } from '../../sporttia/AppContext';
import { SttDialog } from '../../sporttia/dialogs/SttDialog';

type User = {
    id: number;
    fullName?: string;
};

type Mship = {
    id: number;
    fullName?: string;
    user?: User;
};

type Bono = {
    id: number;
    name: string;
};

type Boleto = {
    id: number;
    consumed: number;
    amount: number;
    bono: Bono;
};

type BookingResponse = {
    booking: {
        facility: { id: number };
        ini: string;
        end: string;
    };
};

type FaresResponse = {
    individualBonos?: Boleto[];
    fullBonos?: Boleto[];
};

type UseBonoDialogProps = {
    open: boolean;
    defaultMship?: Mship;
    bookingId: number;
    onClose: () => void;
};

/**
 * Dialog that allows sport center admin to consume a ticket in the name of chosen user.
 */
export default function UseBonoDialog({
    open,
    defaultMship,
    bookingId,
    onClose,
}: UseBonoDialogProps): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const [mshipChosen, setMshipChosen] = useState<Mship | undefined>();
    const [userIndividualBonos, setUserIndividualBonos] = useState<Boleto[]>(
        [],
    );
    const [userFullBonos, setUserFullBonos] = useState<Boleto[]>([]);
    const [selectedBono, setSelectedBono] = useState<{ id: number | null }>({
        id: null,
    });

    /**
     * Load boletos associated to user, facility, booking initial datetime and booking end datetime.
     */
    const loadBonos = (): void => {
        if (
            ((mshipChosen && mshipChosen.user && mshipChosen.user.id) ||
                (mshipChosen && mshipChosen.id)) &&
            bookingId
        ) {
            cxt?.api('GET', `/bookings/${bookingId}`, {
                params: { trash: true },
                success: (r: BookingResponse) => {
                    cxt.api('GET', `/bookings/fares`, {
                        params: {
                            idFacility: r.booking.facility.id,
                            ini: r.booking.ini,
                            end: r.booking.end,
                            idUser: mshipChosen.user
                                ? mshipChosen.user.id
                                : mshipChosen.id,
                        },
                        success: (response: FaresResponse) => {
                            if (response.individualBonos) {
                                setUserIndividualBonos(
                                    response.individualBonos,
                                );
                            }
                            if (response.fullBonos) {
                                setUserFullBonos(response.fullBonos);
                            }
                        },
                    });
                },
            });
        }
    };

    /**
     * If "defaultUser" value was changed, "userChosen" value will be update.
     */
    useEffect(() => {
        if (defaultMship) setMshipChosen(defaultMship);
    }, [defaultMship]);

    /**
     * If "mshipChosen" value was changed, "userBoletos" value will be update loading boletos with loadBoletos().
     */
    useEffect(() => {
        if (open) {
            if (mshipChosen) loadBonos();
            else {
                setUserFullBonos([]);
                setUserIndividualBonos([]);
            }
        }
    }, [mshipChosen]);

    /**
     * If is possible always will be reload bonus data when open dialog.
     */
    useEffect(() => {
        if (open) loadBonos();
    }, [open]);

    /**
     * Use selected bono by user.
     */
    const useBono = (): void => {
        if (!selectedBono.id) return;

        cxt?.api('POST', `/bonos/boletos/${selectedBono.id}/movs`, {
            params: {
                idBooking: bookingId,
            },
            success: () => {
                cxt.showMessage('S', t('ConsumedBono'));
                onClose();
                setSelectedBono({ id: null });
            },
        });
    };

    // -----| Render |-----
    return (
        <SttDialog
            title={t('UseFare')}
            open={open}
            onClose={onClose}
            content={
                <Grid container spacing={3}>
                    {/* Mship selector. */}
                    <SttSelectMship
                        mship={mshipChosen as any}
                        onSelect={(mship: any) => setMshipChosen(mship)}
                    />

                    {/* User bonos list. */}
                    <Grid size={{ xs: 12 }}>
                        {userIndividualBonos.length > 0 ||
                        userFullBonos.length > 0 ? (
                            <Paper>
                                <List
                                    component="nav"
                                    aria-label="secondary mailbox folders"
                                >
                                    {userIndividualBonos.map(
                                        (indvBono) =>
                                            indvBono.consumed !==
                                                indvBono.amount && (
                                                <ListItemButton
                                                    selected={
                                                        selectedBono.id ===
                                                        indvBono.id
                                                    }
                                                    onClick={() =>
                                                        setSelectedBono(
                                                            indvBono,
                                                        )
                                                    }
                                                    key={indvBono.id}
                                                >
                                                    <ListItemText
                                                        primary={`${indvBono.bono.name} [${indvBono.consumed}/${indvBono.amount}]`}
                                                    />
                                                </ListItemButton>
                                            ),
                                    )}
                                    {userFullBonos.map(
                                        (fullBono) =>
                                            fullBono.consumed !==
                                                fullBono.amount && (
                                                <ListItemButton
                                                    selected={
                                                        selectedBono.id ===
                                                        fullBono.id
                                                    }
                                                    onClick={() =>
                                                        setSelectedBono(
                                                            fullBono,
                                                        )
                                                    }
                                                    key={fullBono.id}
                                                >
                                                    <ListItemText
                                                        primary={`${fullBono.bono.name} [${fullBono.consumed}/${fullBono.amount}]`}
                                                    />
                                                </ListItemButton>
                                            ),
                                    )}
                                </List>
                            </Paper>
                        ) : (
                            <Paper>
                                <Box p={2}>{t('UnavailableUserBonos')}</Box>
                            </Paper>
                        )}
                    </Grid>
                </Grid>
            }
            buttons={[
                {
                    show: selectedBono.id !== null,
                    caption: t('Consume'),
                    type: 'accept',
                    onClick: useBono,
                },
                {
                    type: 'close',
                    onClick: onClose,
                },
            ]}
        />
    );
}
